"use client";

import {
  useState,
  useRef,
  useEffect,
  type ReactNode,
  type FC,
  type Ref,
} from "react";
import { AnimatePresence, motion } from "motion/react";
import { isString, isWindowDefined } from "@huub-web/utils";

import { Flex } from "../Flex";
import { Carousel, type CarouselRef } from "../Carousel";
import { Image } from "../Image";
import { Tooltip } from "../Tooltip";
import type { GalleryImage } from "../Gallery/Gallery";
import { IconButton } from "../IconButton";
import { Container, type ContainerProps } from "../Container";
import { useDelayedState, useUuid } from "../../hooks";
import { POI } from "../POI";
import { mergeRefs } from "../../utils";
import { Text } from "../Text";
import { Stack } from "../Stack";
import { NavigationHitArea } from "./components/NavigationHitArea";

import {
  fullImageStyle,
  gridNavigationStyle,
  gridThumbnailStyle,
  containerStyle,
  fullCarouselStyle,
  gridSelectedImageStyle,
  poiStyle,
  gridSelectedImageContainerStyle,
} from "./GalleryCarousel.css";
import { childStyle } from "../Stack/Stack.css";

type GalleryCarouselItem = GalleryImage & {
  reference?: string;
};

export type GalleryCarouselProps = {
  items: GalleryCarouselItem[];
  default?: number;
  variant?: "grid" | "carousel";
  navigation?: "arrows" | "none";
  container?: ContainerProps["size"];
  loading?: boolean;
  loadingChildren?: ReactNode;
  loadingChildrenCount?: number;
  fadeOutInactive?: boolean;
  carouselClassName?: string;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

export const GalleryCarousel: FC<GalleryCarouselProps> = ({
  items = [],
  default: defaultIndex = 0,
  variant = "grid",
  navigation = "none",
  container = "default",
  loading = false,
  loadingChildren,
  fadeOutInactive = false,
  carouselClassName,
  ref,
}) => {
  const currentIndexRef = useRef(defaultIndex);
  const [currentIndex, setCurrentIndex] = useState(defaultIndex);

  const id = useUuid();
  const carouselRef = useRef<CarouselRef>(null);
  const internalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // console.log(
    //   "[GalleryCarousel] currentIndex changed:",
    //   currentIndex,
    //   "total items:",
    //   items.length,
    // );

    currentIndexRef.current = currentIndex;
  }, [currentIndex, items.length]);

  useEffect(() => {
    if (!isWindowDefined() || loading) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        e.stopPropagation();
        handlePrevious();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        e.stopPropagation();
        handleNext();
      }
    };

    internalRef.current?.addEventListener("keydown", handleKeyDown);

    return () => {
      internalRef.current?.removeEventListener("keydown", handleKeyDown);
    };
  }, [items.length, loading]);

  const handlePrevious = () => {
    if (!carouselRef.current) {
      return;
    }

    const newIndex = carouselRef.current.toPrevious();
    // console.log("[GalleryCarousel] handlePrevious - new index:", newIndex);

    if (variant === "grid") {
      setCurrentIndex(newIndex);
    }
  };

  const handleNext = () => {
    if (!carouselRef.current) {
      return;
    }
    const newIndex = carouselRef.current.toNext();
    // console.log("[GalleryCarousel] handleNext - new index:", newIndex);

    if (variant === "grid") {
      setCurrentIndex(newIndex);
    }
  };

  const handleCarouselIndexChange = (index: number) => {
    if (index === currentIndexRef.current || variant === "grid") {
      return;
    }

    // console.log(
    //   "[GalleryCarousel] onIndexChange called with:",
    //   index,
    //   "current:",
    //   currentIndex,
    // );

    setCurrentIndex(index);
  };

  const [title, setTitle] = useDelayedState(
    isString(items?.[currentIndex]) ? "" : (items?.[currentIndex]?.title ?? ""),
    500,
  );

  useEffect(() => {
    setTitle(
      isString(items[currentIndex])
        ? ""
        : (items?.[currentIndex]?.title ??
            items?.[currentIndex]?.reference ??
            ""),
    );
  }, [currentIndex, items]);

  const handleThumbnailClick = (index: number) => {
    setCurrentIndex(index);
    if (variant === "grid") {
      if (carouselRef.current) {
        carouselRef.current.setIndex(index);
      }
    }
  };

  const [delayedCurrentIndex, setDelayedCurrentIndex] = useDelayedState(
    currentIndex,
    variant === "carousel" ? 250 : 0,
  );

  useEffect(() => {
    setDelayedCurrentIndex(currentIndex);
  }, [currentIndex]);

  if (items.length === 0 && !loading) {
    return null;
  }

  return (
    <Flex
      direction="column"
      gap="xs"
      ref={mergeRefs(internalRef, ref)}
      className={containerStyle}
    >
      {variant === "grid" && (
        <>
          <div className={gridSelectedImageContainerStyle}>
            {/* <Image
                src={
                  typeof items[currentIndex] === "string"
                    ? items[currentIndex]
                    : items[currentIndex].url
                }
                className={blurImageStyle}
              /> */}
            <Image
              src={
                typeof items[currentIndex] === "string"
                  ? items[currentIndex]
                  : items[currentIndex].url
              }
              width="auto"
              height="100%"
              alt={
                typeof items[currentIndex] === "string"
                  ? undefined
                  : (items[currentIndex].title ?? items[currentIndex].reference)
              }
              className={gridSelectedImageStyle}
              borderRadius
              outline
              // fit="contain"
            >
              {navigation === "arrows" && items.length > 1 && (
                <Flex
                  align="center"
                  justify="space-between"
                  gap="xs"
                  className={gridNavigationStyle}
                >
                  <NavigationHitArea
                    variant="left"
                    onClick={handlePrevious}
                    disabled={loading}
                  />
                  <NavigationHitArea
                    variant="right"
                    onClick={handleNext}
                    disabled={loading}
                  />
                </Flex>
              )}
            </Image>
          </div>

          {items.length > 1 && (
            <Carousel
              ref={carouselRef}
              filler={false}
              fireIndexChange={false}
              overlapColor="background"
            >
              {items.map((item, i) => (
                <Tooltip
                  key={i}
                  value={
                    typeof item === "string"
                      ? undefined
                      : (item.title ?? item.reference)
                  }
                >
                  <Image
                    id={`${id}-${i}`}
                    className={gridThumbnailStyle()}
                    src={
                      typeof item === "string"
                        ? item
                        : (item.thumbnail ?? item.url)
                    }
                    alt={
                      typeof item === "string"
                        ? undefined
                        : (item.title ?? item.reference)
                    }
                    borderRadius
                    forceHover={i === currentIndex}
                    onClick={() => handleThumbnailClick(i)}
                    outline
                  />
                </Tooltip>
              ))}
            </Carousel>
          )}
        </>
      )}

      {variant === "carousel" && (
        <>
          {navigation === "arrows" && (
            <Container size={container}>
              <Flex justify="space-between" align="center">
                <Stack wrap={false}>
                  <AnimatePresence>
                    <motion.div
                      className={childStyle()}
                      key={title}
                      initial={{
                        opacity: 0,
                        y: 10,
                      }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        transition: { delay: 0.2 },
                      }}
                      exit={{
                        opacity: 0,
                        y: -10,
                      }}
                      transition={{ duration: 0.2 }}
                    >
                      <Text as="h3" variant="displayM" bold>
                        {title}
                      </Text>
                    </motion.div>
                  </AnimatePresence>
                </Stack>
                <Flex align="center" gap="xs" padding="m">
                  <IconButton
                    variant="opaque"
                    border="default"
                    icon="chevronLeft"
                    onClick={handlePrevious}
                    rounded
                    disabled={loading}
                  />
                  <IconButton
                    variant="opaque"
                    border="default"
                    icon="chevronRight"
                    onClick={handleNext}
                    rounded
                    disabled={loading}
                  />
                </Flex>
              </Flex>
            </Container>
          )}

          <Carousel
            ref={carouselRef}
            variant="centered"
            gap="m"
            contentClassName={fullCarouselStyle}
            onIndexChange={handleCarouselIndexChange}
            overlapSize="none"
            disabled={loading}
            className={carouselClassName}
          >
            {loading &&
              Array.from({ length: 10 }).map((_, i) => (
                <Image
                  id={`${id}-${i}`}
                  key={i}
                  borderRadius
                  width="auto"
                  height="100%"
                  className={fullImageStyle({ placeholder: true })}
                  active={i === 5}
                  backgroundColor="navigationTint"
                >
                  {i === 5 && loadingChildren}
                </Image>
              ))}

            {!loading &&
              items.map((item, i) => {
                const src = isString(item) ? item : item.url;
                const alt = isString(item)
                  ? undefined
                  : (item.title ?? item.reference);
                const hotspots = isString(item) ? [] : item.hotspots;

                return (
                  <Image
                    id={`${id}-${i}`}
                    src={src}
                    key={i}
                    alt={alt}
                    borderRadius
                    className={fullImageStyle({
                      inactive: fadeOutInactive
                        ? i !== delayedCurrentIndex
                        : undefined,
                    })}
                    active={i === delayedCurrentIndex}
                    outline
                  >
                    <AnimatePresence>
                      {hotspots?.map((hotspot, y) => (
                        <POI
                          key={`${i}-${y}`}
                          className={poiStyle}
                          left={hotspot.left}
                          top={hotspot.top}
                          delay={y * 0.2 + 0.2}
                          animate={i === delayedCurrentIndex}
                          tooltip={
                            <Flex direction="column" gap="xxs">
                              <Text bold>{hotspot.title}</Text>
                              <Text variant="bodyS" color="currentColor75">
                                {hotspot.subtitle}
                              </Text>
                            </Flex>
                          }
                          icon="aiFilled"
                          iconColor="gradient"
                        />
                      ))}
                    </AnimatePresence>
                  </Image>
                );
              })}
          </Carousel>
        </>
      )}
    </Flex>
  );
};
